import React from "react";
import { ICellRendererParams, RowNode } from "ag-grid-community";
import { ProductCollectionTileViewGroupState } from "../../product-collection-tile-view/product-collection-tile-view-group-state";
import { merge, $ } from "glamor";
import theme from "styles/theme";
import { AGGREGATE_COLUMN_PREFIX, getBlankTextValue, UNSET_GROUP_KEY } from "helpers/server-rollup";
import { extractAssortmentNameIfNeeded, isStringAggregationFunction } from "./render-utils";
import { getValueFormatter, serverRollupCurrencyPercentValueFormatter } from "helpers/product-collection";

interface IProps extends ICellRendererParams {}

const getGroupDisplayName = (row: RowNode, acc: string[] = []): string => {
    if (row.level === 0) {
        return acc.concat([row.key]).reverse().join(" → ");
    }

    acc.push(row.key);
    return getGroupDisplayName(row.parent, acc);
};

export const GroupRowInnerRenderer: React.FunctionComponent<IProps> = (params): any => {
    const { value, valueFormatted, node, data, colDef, context, columnApi } = params;
    const { valueCols } = (node as any).beans.rowModel.storeParams;
    const styles = merge(
        $(" .groupHeader", {
            backgroundColor: theme.palette.lightestGrey,
            borderRadius: "5px",
            margin: "0 4px",
            minHeight: "72px",
            overflow: "auto",
        }),
        $(" .groupHeader h3", {
            margin: "0",
            padding: "0 16px",
            fontSize: "28px",
            lineHeight: "72px",
        }),
        $(" .groupTitle", {
            paddingLeft: "20px",
            height: "36px",
            lineHeight: "36px",
            fontSize: "17px",
        }),
        $(" .groupStats", {
            display: "flex",
        }),
        $(" .groupStats_level1", {
            paddingLeft: "16px",
        }),
        $(" .groupStats_level2", {
            paddingLeft: "32px",
        }),
        $(" .groupStats_level3", {
            paddingLeft: "48px",
        }),
        $(" .groupStat", {
            padding: "0 20px",
        }),
        $(" .groupStatTitle", {
            fontWeight: "bold",
        })
    );
    const rawItemDisplayProperty = context.tileGridContext?.rawItemDisplayProperty;
    const details = valueCols.map((valueCol) => {
        const aggregateField = data.fields[`${AGGREGATE_COLUMN_PREFIX}${valueCol.field}`];
        const fieldType = aggregateField?.hasOwnProperty("number_value") ? "number_value" : "string_value";
        const groupValue = aggregateField?.[fieldType];
        const schemaField = rawItemDisplayProperty.find((field) => field && field.field_name === valueCol.field);
        const isStringAgg = context?.isServerRollup && isStringAggregationFunction(valueCol);
        const groupValueFormatted = isStringAgg
            ? serverRollupCurrencyPercentValueFormatter(schemaField, valueCol.aggFunc, groupValue)
            : getValueFormatter(schemaField)({
                  value: groupValue,
                  columnApi,
                  node,
                  colDef: node.rowGroupColumn.getColDef(),
              });

        return (
            <ProductCollectionTileViewGroupState
                key={valueCol.id}
                name={valueCol.displayName || valueCol.id}
                value={groupValueFormatted}
            />
        );
    });

    let groupTitle = valueFormatted || value;
    groupTitle =
        groupTitle === UNSET_GROUP_KEY ? getBlankTextValue() : extractAssortmentNameIfNeeded(groupTitle, colDef, node);
    const shouldShowItemCount = node.group && data;
    return (
        <div {...styles} className="groupHeader">
            <div className="groupTitle">
                {groupTitle}
                {shouldShowItemCount && <span style={{ paddingLeft: 4 }}> ({data.itemCount || 0})</span>}
            </div>
            <div className="groupStats">{details}</div>
        </div>
    );
};
